/**
 * C02 Battle Pass — cross-feature window event listeners.
 *
 * Wires retention-event dispatches from C01 streak / C04 badge / C05 dayone-hook
 * into seasonXP grants. This keeps the producers (streak.ts, badges.ts,
 * dayone-hook.ts) cleanly decoupled — they emit a window event that's already
 * useful for other surfaces (mascot etc.), and the season-pass listener just
 * credits XP as a side-channel observer.
 *
 * Why a separate file (not folded into home.ts):
 *   - Idempotent install (called from main.ts boot OR home render — listener
 *     is global, not per-render). Avoids stacking dup listeners on home
 *     re-render.
 *   - Testable in isolation — tests construct the listener directly with a
 *     stub progress + dispatchEvent and assert XP credit.
 *
 * 🔥 學習與遊戲分離: this file is 學習端 only; the boss-defeat XP source is
 * gated on a learn-side game-end event the battle scene MAY emit
 * (`ef:boss-defeated`) — if it never fires, season-pass simply receives 0
 * XP from that channel and the kid still progresses via answer / streak.
 */

import type { PlayerProgress } from '../../shared/types';
import { loadProgress, saveProgress } from '../../shared/progress';
import { addSeasonXP, SEASON_XP_HINTS, type SeasonXpSource } from './season-pass';

/** Internal flag — install() is idempotent. */
let installed = false;

/**
 * Install global window listeners. Idempotent: calling twice is a no-op.
 * Caller is `main.ts` (preferred) OR `home.ts` first-render (fallback).
 *
 * Returns the uninstall function, primarily for tests so each test case can
 * isolate its listener.
 */
export function installSeasonPassHooks(): () => void {
  if (installed) return () => { /* idempotent — caller will get a real cleanup on the original install call */ };
  if (typeof window === 'undefined') return () => { /* noop in non-DOM env */ };

  const onStreakMilestone = (e: Event) => {
    // C01 emits { tier, days, longest }. Only credit on milestone CROSSING
    // (the event already implies that — streak.ts only fires it on cross).
    handleEvent('streak-milestone', SEASON_XP_HINTS.streakMilestoneXp, (e as CustomEvent).detail);
  };
  const onBadgeUnlocked = (e: Event) => {
    handleEvent('badge-unlock', SEASON_XP_HINTS.badgeUnlockXp, (e as CustomEvent).detail);
  };
  const onDayoneClaimed = (e: Event) => {
    handleEvent('dayone-claim', SEASON_XP_HINTS.dayOneClaimXp, (e as CustomEvent).detail);
  };
  const onBossDefeated = (e: Event) => {
    handleEvent('boss-defeat', SEASON_XP_HINTS.bossDefeatXp, (e as CustomEvent).detail);
  };
  const onPerfectExam = (e: Event) => {
    handleEvent('perfect-exam', SEASON_XP_HINTS.perfectExamXp, (e as CustomEvent).detail);
  };

  window.addEventListener('ef:streak-milestone', onStreakMilestone);
  window.addEventListener('ef:badge-unlocked', onBadgeUnlocked);
  window.addEventListener('ef:dayone-claimed', onDayoneClaimed);
  window.addEventListener('ef:boss-defeated', onBossDefeated);
  window.addEventListener('ef:perfect-exam', onPerfectExam);

  installed = true;
  return () => {
    window.removeEventListener('ef:streak-milestone', onStreakMilestone);
    window.removeEventListener('ef:badge-unlocked', onBadgeUnlocked);
    window.removeEventListener('ef:dayone-claimed', onDayoneClaimed);
    window.removeEventListener('ef:boss-defeated', onBossDefeated);
    window.removeEventListener('ef:perfect-exam', onPerfectExam);
    installed = false;
  };
}

/**
 * Shared event-handler body. Loads current progress, credits seasonXP,
 * persists, and re-dispatches `ef:season-xp-granted` so the home banner
 * can re-render itself on the fly.
 */
function handleEvent(source: SeasonXpSource, amount: number, _detail: unknown): void {
  void _detail;
  if (amount <= 0) return;
  const p = loadProgress();
  if (!p) return; // No active profile — silently drop.
  const before = p.seasonPass?.xpThisSeason ?? 0;
  addSeasonXP(p, amount, source);
  const after = p.seasonPass?.xpThisSeason ?? 0;
  if (after === before) return; // No credit (e.g. malformed amount); skip save+broadcast
  try {
    saveProgress(p);
  } catch {
    // ignore — see addSeasonXP / claimTier
  }
  try {
    window.dispatchEvent(new CustomEvent('ef:season-xp-granted', { detail: { source, amount, total: after } }));
  } catch {
    // jsdom without CustomEvent shim — ignore.
  }
}

/**
 * Direct call (no event) helper for places that DON'T have a window event
 * to leverage (e.g. an internal lesson-runner that wants to credit a
 * perfect-quiz bonus). Wraps loadProgress + addSeasonXP + saveProgress
 * in one shot. Returns the new total or null if no progress is loaded.
 */
export function grantSeasonXp(amount: number, source: SeasonXpSource): number | null {
  const p: PlayerProgress | null = loadProgress();
  if (!p) return null;
  const before = p.seasonPass?.xpThisSeason ?? 0;
  addSeasonXP(p, amount, source);
  const after = p.seasonPass?.xpThisSeason ?? 0;
  if (after === before) return after;
  try {
    saveProgress(p);
  } catch {
    // ignore
  }
  try {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('ef:season-xp-granted', { detail: { source, amount, total: after } }));
    }
  } catch {
    // ignore
  }
  return after;
}
